import React from "react";
import { useMutation } from "@apollo/client";

import { Typography } from "@mui/material";

import MoovsDialog from "../../globals/MoovsDialog";
import { getErrorMessage } from "moovsErrors/getErrorMessage";
import { REQUEST_CANCELLATION_TO_TRIP } from "globals/graphql";
import { useAnalytics, useSnackbar } from "globals/hooks";
import { RequestTripCancellationPayload, Trip } from "types";

type RequestCancellationToTripDialogProps = {
  open: boolean;
  onClose: () => void;
  trip: Trip;
  isReservationRequest?: boolean;
};

function RequestCancellationToTripDialog(
  props: RequestCancellationToTripDialogProps
) {
  const { open, onClose, trip, isReservationRequest } = props;

  // hooks
  const snackbar = useSnackbar();
  const { track } = useAnalytics();

  // mutations
  const [requestTripCancellation, { loading }] = useMutation<{
    requestTripCancellation: RequestTripCancellationPayload;
  }>(REQUEST_CANCELLATION_TO_TRIP, {
    onCompleted(data) {
      const result = data.requestTripCancellation.result;

      if (result.__typename === "RequestTripCancellationFailure") {
        snackbar.error(result.reason);
        return;
      }

      track("cancellation_requested");
      onClose();
    },
    onError(error) {
      const errorMessage = getErrorMessage(error);
      snackbar.error(errorMessage);
    },
  });

  // handlers
  const handleRequestTripCancellation = () => {
    requestTripCancellation({
      variables: {
        input: {
          tripId: trip.id,
        },
      },
    });
  };

  return (
    <MoovsDialog
      hideLoadingIndicator={false}
      acceptDisabled={loading}
      open={open}
      onClose={onClose}
      dialogTitle="Cancel Trip"
      size="xs"
      onAccept={handleRequestTripCancellation}
      acceptButtonText={isReservationRequest ? "Yes, cancel reservation" : "Yes, request cancellation"}
      acceptButtonColor="error"
      closeButtonText="No, keep"
    >
      <Typography variant="body2" p={2}>
        {isReservationRequest
          ? "Are you sure you want to cancel this trip? The operator has not confirmed this request yet, the cancellation will go through automatically."
          : "Are you sure you want to cancel trip? A cancellation request will be sent to the operator."
        }
      </Typography>
    </MoovsDialog>
  );
}

export default RequestCancellationToTripDialog;
